import {
  RateType,
  type CurrencyAmount,
  type RawRateSummary,
} from "@/generated/search.openapi";

export const isLiteapiCashRateType = (rateType: RateType | undefined) =>
  rateType === RateType.LiteapiCash;

export const isGondolaCashRateType = (rateType: RateType | undefined) =>
  rateType === RateType.GondolaCash;

/**
 * Validation refreshes a persisted supplier rate, whose concrete rate type is
 * Direct/Travelport even when the user selected its Gondola Cash alias. Keep
 * that client-side product choice while the backend continues routing by the
 * persisted gondolaRateId.
 */
export const preserveSelectedRateType = (
  selectedRateType: RateType | undefined,
  validatedRateType: RateType
): RateType =>
  isGondolaCashRateType(selectedRateType)
    ? RateType.GondolaCash
    : validatedRateType;

export const isLegacyLiteapiRateType = (rateType: RateType | undefined) =>
  rateType === RateType.Liteapi;

export const isExplicitLiteapiSplitRateType = (
  rateType: RateType | undefined
) => isLiteapiCashRateType(rateType) || isGondolaCashRateType(rateType);

export const isLiteapiBackedRateType = (rateType: RateType | undefined) =>
  isExplicitLiteapiSplitRateType(rateType);

export const isLiteapiCardChargeRateType = (rateType: RateType | undefined) =>
  isLiteapiCashRateType(rateType);

export const isNonGondolaCashLiteapiRateType = (
  rateType: RateType | undefined
) => isLegacyLiteapiRateType(rateType) || isLiteapiCashRateType(rateType);

/**
 * Every rate type whose inventory comes from LiteAPI, including the Gondola
 * Cash split rate. Use this, not `isNonGondolaCashLiteapiRateType`, whenever
 * the question is "is this stay booked into the hotel's own system?", because a
 * `gondola cash` rate paid by card (`used_gondola_credits=false`) is still
 * LiteAPI inventory and still must not claim a direct booking.
 */
export const isLiteapiSuppliedRateType = (rateType: RateType | undefined) =>
  isNonGondolaCashLiteapiRateType(rateType) || isGondolaCashRateType(rateType);

/** `Booking.booking_process` for LiteAPI stays (`BookingProcess.LITEAPI`). */
export const LITEAPI_BOOKING_PROCESS = "LITEAPI";

/**
 * Whether a booking was fulfilled through LiteAPI. Checks the booking process
 * as well as the rate type because `rate_summary`, and therefore `rateType`,
 * is null on the degraded booking-conversion paths in `booking_helpers.py`,
 * and an unknown rate type must not fall through to the direct-booking copy.
 */
export const isLiteapiBooking = ({
  rateType,
  bookingProcess,
}: {
  rateType: RateType | undefined;
  bookingProcess?: string;
}) =>
  isLiteapiSuppliedRateType(rateType) ||
  bookingProcess === LITEAPI_BOOKING_PROCESS;

/**
 * How a rate is booked, surfaced as the "Direct" / "Third Party" tag on every
 * rate row.
 *
 * - `direct`: booked into the hotel's own system, so it earns hotel points and
 *   elite-night credit.
 * - `third-party`: wholesale / OTA inventory booked with Gondola as the
 *   merchant of record, so it does not.
 */
export type RateChannel = "direct" | "third-party";

const LITEAPI_CHANNEL_RATE_TYPES: ReadonlySet<string> = new Set([
  RateType.Liteapi,
  RateType.LiteapiCash,
  RateType.GondolaCash,
]);

const THIRD_PARTY_RATE_TYPES: ReadonlySet<string> = new Set([
  RateType.Liteapi,
  RateType.LiteapiCash,
  RateType.GondolaCash,
  RateType.Expedia,
  RateType.ExpediaEarnings,
  // Booking.com inventory sold through Travelport. Despite the `travelport`
  // prefix it is NOT a direct booking: the backend excludes it from both hotel
  // points and status nights (see `rate_converters.py`, which special-cases
  // TRAVELPORT_BOOKING_DOT_COM out of the earnings/status-night computation).
  RateType.TravelportBookingCom,
]);

/**
 * Rate types booked into the hotel's own system. The Travelport entries mirror
 * `SUPPORTED_RATE_TYPES` in `api_search/lib/travelport/travelport_helpers.py`,
 * i.e. exactly the Travelport rates that surface in the UI and earn points.
 *
 * Deliberately an explicit allowlist rather than a `travelport` prefix match:
 * a prefix rule silently opts in every future variant, and `travelport
 * booking.com` proves that not every Travelport rate is a direct booking.
 */
const DIRECT_RATE_TYPES: ReadonlySet<string> = new Set([
  RateType.DirectCash,
  RateType.DirectPoints,
  RateType.Travelport,
  RateType.TravelportAaa,
  RateType.TravelportAarp,
  RateType.TravelportMember,
  RateType.TravelportAbc,
  RateType.TravelportAbcVip,
  RateType.TravelportAbcMember,
  RateType.TravelportAbcVipMember,
]);

export type RateChannelOptions = {
  /** Concrete supplier inventory behind a product-facing Gondola Cash alias. */
  inventoryRateType?: RateType;
  /**
   * LiteAPI's loyalty feed is Travelport-backed. When a LiteAPI row came from
   * that feed, the backend marks it `member_rate=true`; surface those as direct
   * bookings, matching Travelport member rates.
   */
  isLiteapiMemberRate?: boolean;
  /**
   * The property is one of the Travelport-only brands — Mr & Mrs Smith (Hyatt)
   * or SLH/LX (Hilton). See `getTravelportOnlyBrandConfig`.
   *
   * At these properties Travelport is only the pipe: `additional_rates_v3.py`
   * deliberately routes cash through Travelport because Gondola earns no
   * commission on the chain's direct cash, and the reservation lands with
   * MMS/SLH rather than with Hyatt/Hilton — so it earns no chain points. Cash
   * is therefore third-party here despite a `travelport *` rate type, the same
   * way `travelport booking.com` is third-party everywhere.
   *
   * The points row is untouched: it is a real award redemption on the chain's
   * own site, which is why the backend keeps serving it alongside the
   * Travelport cash rates.
   */
  isTravelportOnlyBrand?: boolean;
};

/**
 * Classifies a rate as direct or third-party. Returns `undefined` for an
 * unrecognized rate type so a new supplier is left untagged rather than
 * mislabeled as "Direct" (claiming direct when it isn't is the costly error).
 */
export const getRateChannel = (
  rateType: RateType | undefined,
  {
    inventoryRateType,
    isLiteapiMemberRate = false,
    isTravelportOnlyBrand = false,
  }: RateChannelOptions = {}
): RateChannel | undefined => {
  if (!rateType) return undefined;
  if (rateType === RateType.GondolaCash && inventoryRateType) {
    return getRateChannel(inventoryRateType, {
      isLiteapiMemberRate,
      isTravelportOnlyBrand,
    });
  }
  if (isLiteapiMemberRate && LITEAPI_CHANNEL_RATE_TYPES.has(rateType)) {
    return "direct";
  }
  if (THIRD_PARTY_RATE_TYPES.has(rateType)) return "third-party";
  if (DIRECT_RATE_TYPES.has(rateType)) {
    if (isTravelportOnlyBrand && rateType !== RateType.DirectPoints) {
      return "third-party";
    }
    return "direct";
  }
  return undefined;
};

export const isDirectRate = (
  rateType: RateType | undefined,
  options?: RateChannelOptions
) => getRateChannel(rateType, options) === "direct";

/**
 * A Gondola Cash product can be backed by either legacy LiteAPI inventory or
 * by a direct supplier rate. Only the latter keeps hotel loyalty earnings.
 */
export const isLoyaltyEligibleGondolaCashRate = (
  rateType: RateType | undefined,
  options?: RateChannelOptions
) => {
  const inventoryRateType = options?.inventoryRateType;

  return (
    isGondolaCashRateType(rateType) &&
    // Loyalty eligibility is a product-level promise and therefore requires
    // the concrete Direct/Travelport inventory written during validation. The
    // LiteAPI member-feed marker still controls the channel badge, but cannot
    // turn a legacy or missing-inventory Gondola Cash alias into an eligible
    // supplier-backed stay.
    inventoryRateType !== undefined &&
    inventoryRateType !== RateType.DirectPoints &&
    isDirectRate(inventoryRateType, {
      isTravelportOnlyBrand: options?.isTravelportOnlyBrand,
    })
  );
};

export type RateEarningsPresentation = {
  useGondolaCredits: boolean;
  includeHotelLoyalty: boolean;
  cardTotalEarnings?: CurrencyAmount;
};

/**
 * Applies the same earnings policy to a hotel-details rate row and its modal.
 * A Gondola Cash redemption never earns more Gondola Cash or card points. A
 * direct supplier alias keeps only the hotel-loyalty value; legacy/third-party
 * inventory shows no earnings. Ordinary cash rows preserve their existing
 * aggregate total.
 */
export const getRateEarningsPresentation = (
  rate: RawRateSummary,
  {
    isTravelportOnlyBrand = false,
  }: Pick<RateChannelOptions, "isTravelportOnlyBrand"> = {}
): RateEarningsPresentation => {
  const useGondolaCredits = isGondolaCashRateType(rate.rateType);
  const includeHotelLoyalty =
    !useGondolaCredits ||
    isLoyaltyEligibleGondolaCashRate(rate.rateType, {
      inventoryRateType: rate.inventoryRateType,
      isTravelportOnlyBrand,
    });

  return {
    useGondolaCredits,
    includeHotelLoyalty,
    cardTotalEarnings: useGondolaCredits
      ? includeHotelLoyalty
        ? rate.earningsPoints?.cashEquivalent
        : undefined
      : rate.totalEarnings,
  };
};

export const isGondolaCashBookingOutcome = ({
  rateType,
  usedGondolaCredits,
}: {
  rateType: RateType | undefined;
  usedGondolaCredits?: boolean;
}) => usedGondolaCredits ?? isGondolaCashRateType(rateType);
